import { QueryEMX2 } from "molgenis-components";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import useErrorHandler from "../composables/errorHandler";
import {
  validateDataModel,
  validateFilterFacets,
  validateServiceColumns,
} from "../filter-config/filterConfigValidator";
import { initialFilterFacets } from "../filter-config/initialFilterFacets";
import { i18n } from "../i18n/i18n";
import { deploymentLabels } from "../property-config/deploymentLabels";
import initialBiobankColumns from "../property-config/initialBiobankColumns";
import initialBiobankReportColumns from "../property-config/initialBiobankReportColumns";
import initialCollectionColumns from "../property-config/initialCollectionColumns";
import initialDataModel from "../property-config/initialDataModel";
import initialLandingpage from "../property-config/initialLandingpage";
import initialNetworkReportColumns from "../property-config/initialNetworkReportColumns";
import initialServiceProviderReportColumns from "../property-config/initialServiceProviderReportColumns";
import initialServiceReportColumns from "../property-config/initialServiceReportColumns";
import initialServiceTableColumns from "../property-config/initialServiceTableColumns";
import serviceContactColumns from "../property-config/serviceContactColumns";
import initialStudyColumns from "../property-config/initialStudyColumns";
/**
 * Settings store is where all the configuration of the application is handled.
 * This means that user config from the database is merged with the defaults here.
 */

const { setError, clearError } = useErrorHandler();

/** How long a route guard waits for the navbar session before giving up. */
const SESSION_READY_TIMEOUT_MS = 5000;

export const useSettingsStore = defineStore("settingsStore", () => {
  const session = ref({});
  const configUpdateStatus = ref(0);
  const currentPage = ref(1);
  const configurationFetched = ref(false);
  const configFilterWarnings = ref([]);
  const configDataModelWarnings = ref([]);
  const configServiceColumnWarnings = ref([]);
  const lastKnownGoodConfig = ref(null);
  const googleAnalyticsId = ref("");
  const viewMode = ref("services"); // "biobank", "collections", or "services"
  // Tracks the value the unknown-deployment warning was logged for, so the
  // computed label can re-evaluate without repeating the warning.
  let lastWarnedDeployment = null;
  const config = ref({
    language: "en",
    graphqlEndpoint: "graphql",
    negotiatorType: "v3",
    negotiatorUrl: "https://isidore-negotiator.acc.bbmri-eric.eu/api/v3/requests",
    negotiatorUsername: "",
    negotiatorPassword: "",
    dataModel: initialDataModel,
    biobankColumns: initialBiobankColumns,
    biobankReportColumns: initialBiobankReportColumns,
    collectionColumns: initialCollectionColumns,
    studyColumns: initialStudyColumns,
    serviceReportColumns: initialServiceReportColumns,
    serviceProviderReportColumns: initialServiceProviderReportColumns,
    serviceContactColumns: serviceContactColumns,
    serviceTableColumns: initialServiceTableColumns,
    networkReportColumns: initialNetworkReportColumns,
    filterFacets: initialFilterFacets,
    biobankCardShowCollections: false,
    collectionsViewEnabled: false,
    landingpage: initialLandingpage,
    pageSize: 12,
    i18n,
    faviconURL: "/isidore-favicon.png",
    logoURL: "/isidore-logo.svg",
    banner: ``,
    footer: ``,
    helpUrl: "https://isidore-project.eu/",
    callLabel: "Call: Andes Virus & ANDV-relevant Hantavirus Research",
    callUrl:
      "https://isidore-project.eu/calls/andes-virus-andv-relevant-hantavirus-research/",
    callPdfLabel: "Download the full call",
    callPdfUrl:
      "https://isidore-project.eu/wp-content/uploads/isidore-ii-tna-andv-call-for-proposals-full-version.pdf",
    matomoUrl: undefined,
    matomoSiteId: undefined,
    deployment: undefined,
  });
  const configurationPromise = ref();

  const showSettings = computed(() => {
    return session.value.roles?.includes("Manager");
  });

  /**
   * Resolves once the navbar has reported a session, so route guards can tell
   * "not signed in" apart from "session has not arrived yet".
   *
   * The session is fetched by the vendored MolgenisSession component in the
   * navbar and reaches us through setSessionInformation(), which is well after
   * the router handles a deep link such as #/configuration. Without this, a
   * Manager opening that URL directly is bounced to the landing page.
   *
   * The race means a backend that never answers costs a short wait rather than
   * a navigation that never completes.
   */
  let markSessionReceived;
  const sessionReceived = new Promise((resolve) => {
    markSessionReceived = resolve;
  });

  function sessionReady() {
    return Promise.race([
      sessionReceived,
      new Promise((resolve) => setTimeout(resolve, SESSION_READY_TIMEOUT_MS)),
    ]);
  }

  const uiText = computed(() => {
    return config.value.i18n[config.value.language];
  });

  const collectionsViewEnabled = computed(() => {
    return config.value.collectionsViewEnabled === true;
  });

  /**
   * Label for the deployment marker in the navbar, empty when no marker should
   * be shown. `VITE_DEPLOYMENT` (baked in at build time) overrides the
   * persisted `deployment` config key, so a production build can force the
   * marker off even when it reads an acceptance database.
   */
  const deploymentLabel = computed(() => {
    const fromEnvironment = import.meta.env.VITE_DEPLOYMENT;
    const configured =
      typeof fromEnvironment === "string" && fromEnvironment.trim()
        ? fromEnvironment
        : config.value.deployment;

    if (typeof configured !== "string" || !configured.trim()) {
      return "";
    }

    const label = deploymentLabels[configured.trim().toLowerCase()];

    if (!label && configured !== lastWarnedDeployment) {
      lastWarnedDeployment = configured;
      console.warn(
        `[directory] unknown deployment value "${configured}", no deployment badge shown`
      );
    }

    return label || "";
  });

  initializeConfig();

  async function initializeConfig() {
    if (configurationFetched.value) return;

    await loadConfig();
  }

  async function loadConfig() {
    configurationFetched.value = false;
    clearError();

    try {
      configurationPromise.value = new QueryEMX2(config.value.graphqlEndpoint)
        .table("_settings")
        .select(["key", "value"])
        .execute();
    } catch (error) {
      setError(error);
      return;
    }

    const response = await configurationPromise.value;

    const savedDirectoryConfig = response._settings.find(
      (setting) => setting.key === "directory"
    );

    if (response._settings.find((setting) => setting.key === "ANALYTICS_ID")) {
      googleAnalyticsId.value = response._settings.find(
        (setting) => setting.key === "ANALYTICS_ID"
      ).value;
    }

    let loadedDirectoryKeys = [];

    if (savedDirectoryConfig?.value) {
      const loadedConfig = JSON.parse(decodeURI(savedDirectoryConfig.value));
      loadedDirectoryKeys = Object.keys(loadedConfig);
      config.value = {
        ...config.value,
        ...loadedConfig,
      };

      // dataModel merges one level deep: a persisted config that overrides
      // only some dataModel keys must not wipe the remaining defaults.
      if (loadedConfig.dataModel) {
        config.value.dataModel = {
          ...initialDataModel,
          ...loadedConfig.dataModel,
          searchFields: {
            ...initialDataModel.searchFields,
            ...loadedConfig.dataModel.searchFields,
          },
        };

        const dataModelValidationErrors = validateDataModel(
          config.value.dataModel
        );

        if (dataModelValidationErrors.length > 0) {
          configDataModelWarnings.value = dataModelValidationErrors;
          config.value = {
            ...config.value,
            dataModel: initialDataModel,
          };

          console.warn(
            "[directory] invalid dataModel in persisted configuration, falling back to defaults",
            dataModelValidationErrors
          );
        } else {
          configDataModelWarnings.value = [];
        }
      }

      // Service display configs: a persisted config still holding the legacy
      // raw GraphQL selection cannot drive the rendering, so fall back to the
      // code defaults rather than rendering an empty service page.
      const serviceColumnWarnings = [];
      const serviceColumnDefaults = {
        serviceReportColumns: initialServiceReportColumns,
        serviceProviderReportColumns: initialServiceProviderReportColumns,
      };

      for (const [configKey, defaultColumns] of Object.entries(
        serviceColumnDefaults
      )) {
        if (loadedConfig[configKey] === undefined) continue;

        const serviceColumnErrors = validateServiceColumns(
          config.value[configKey],
          configKey
        );

        if (serviceColumnErrors.length > 0) {
          serviceColumnWarnings.push(...serviceColumnErrors);
          config.value = {
            ...config.value,
            [configKey]: defaultColumns,
          };

          console.warn(
            `[directory] invalid ${configKey} in persisted configuration, falling back to defaults`,
            serviceColumnErrors
          );
        }
      }

      configServiceColumnWarnings.value = serviceColumnWarnings;

      const filterFacetValidationErrors = validateFilterFacets(
        config.value.filterFacets || []
      );

      if (filterFacetValidationErrors.length > 0) {
        configFilterWarnings.value = filterFacetValidationErrors;
        config.value = {
          ...config.value,
          filterFacets: initialFilterFacets,
        };

        console.warn(
          "[directory] invalid filterFacets in persisted configuration, falling back to defaults",
          filterFacetValidationErrors
        );
      } else {
        configFilterWarnings.value = [];
      }
    }

    // Keep a rollback snapshot of the last valid effective runtime configuration.
    lastKnownGoodConfig.value = JSON.parse(JSON.stringify(config.value));

    console.info("[directory] setting source", {
      hasDirectorySetting: Boolean(savedDirectoryConfig?.value),
      loadedDirectoryKeys,
    });

    // Startup diagnostics: show effective runtime endpoints after config merge.
    console.info("[directory] runtime config", {
      graphqlEndpoint: config.value.graphqlEndpoint,
      negotiatorType: config.value.negotiatorType,
      negotiatorUrl: config.value.negotiatorUrl,
    });

    configurationFetched.value = true;
  }

  function setSessionInformation(newSession) {
    session.value = newSession;
    markSessionReceived();
  }

  async function SaveApplicationConfiguration(configuration) {
    configUpdateStatus.value = 0;

    const updateResult = await new QueryEMX2(
      config.value.graphqlEndpoint
    ).saveSetting("directory", configuration);

    configUpdateStatus.value = updateResult.includes("success") ? 204 : 401;
    await loadConfig();
  }

  async function restoreLastKnownGoodConfiguration() {
    if (!lastKnownGoodConfig.value) {
      return false;
    }

    await SaveApplicationConfiguration(
      JSON.stringify(lastKnownGoodConfig.value)
    );
    return true;
  }

  async function UpdateConfig(newConfig) {
    config.value = newConfig;
  }

  function setViewMode(mode) {
    if (mode === "collections" && !collectionsViewEnabled.value) {
      viewMode.value = "services";
      return;
    }

    viewMode.value = mode;
  }

  function toggleViewMode() {
    if (!collectionsViewEnabled.value) {
      if (viewMode.value === "biobank") {
        viewMode.value = "services";
        return;
      }

      viewMode.value = "biobank";
      return;
    }

    if (viewMode.value === "biobank") {
      viewMode.value = "collections";
      return;
    }

    if (viewMode.value === "collections") {
      viewMode.value = "services";
      return;
    }

    viewMode.value = "biobank";
  }

  return {
    config,
    session,
    googleAnalyticsId,
    configurationFetched,
    configFilterWarnings,
    configDataModelWarnings,
    configServiceColumnWarnings,
    lastKnownGoodConfig,
    configurationPromise,
    configUpdateStatus,
    currentPage,
    viewMode,
    collectionsViewEnabled,
    deploymentLabel,
    setViewMode,
    toggleViewMode,
    showSettings,
    sessionReady,
    uiText,
    initializeConfig,
    SaveApplicationConfiguration,
    restoreLastKnownGoodConfiguration,
    setSessionInformation,
    UpdateConfig,
  };
});
